import React from "react";
import { observer } from "mobx-react-lite";
import { toastStore, type ToastMessage } from "@store/ToastStore";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, XCircle, Info, AlertTriangle, X } from "lucide-react";

const toastIcons: Record<string, React.ReactNode> = {
  success: <CheckCircle size={20} className="chatx-toast-icon text-green-300" />,
  error: <XCircle size={20} className="chatx-toast-icon text-red-300" />,
  info: <Info size={20} className="chatx-toast-icon text-blue-300" />,
  warning: <AlertTriangle size={20} className="chatx-toast-icon text-yellow-300" />,
};

/** How long the action (e.g. «Вернуть») stays available: ticks every 100 ms, frozen while the toast is hovered. */
function useToastRemaining(id: number, total: number | undefined, active: boolean): number {
  const [left, setLeft] = React.useState(() => toastStore.remainingMs(id) ?? total ?? 0);
  React.useEffect(() => {
    if (!active) return;
    const tick = () => setLeft(toastStore.remainingMs(id) ?? 0);
    tick();
    const timer = window.setInterval(tick, 100);
    return () => window.clearInterval(timer);
  }, [id, active]);
  return left;
}

const ToastItem: React.FC<{ toast: ToastMessage }> = ({ toast }) => {
  const hasAction = Boolean(toast.action);
  const left = useToastRemaining(toast.id, toast.duration, hasAction);
  const total = toast.duration ?? 0;
  const seconds = Math.max(1, Math.ceil(left / 1000));
  const progress = total > 0 ? Math.max(0, Math.min(1, left / total)) : 0;
  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 50 }}
      transition={{ duration: 0.2 }}
      className={`
        chatx-toast px-4 py-3 rounded-xl text-sm shadow-lg border
        backdrop-blur-md min-w-[240px] max-w-[min(440px,calc(100vw-3rem))] flex items-start justify-between gap-2
        ${toast.type === "success" ? "chatx-toast--success bg-green-600/80 border-green-300/30" : ""}
        ${toast.type === "error" ? "chatx-toast--error bg-red-600/80 border-red-300/30" : ""}
        ${toast.type === "info" ? "chatx-toast--info bg-blue-600/80 border-blue-300/30" : ""}
        ${toast.type === "warning" ? "chatx-toast--warning bg-yellow-600/80 border-yellow-300/30" : ""}
        ${hasAction ? "chatx-toast--with-action" : ""}
      `}
      onMouseEnter={() => toastStore.pause(toast.id)}
      onMouseLeave={() => toastStore.resume(toast.id)}
    >
      <div className="flex items-start gap-2 min-w-0">
        <span className="mt-0.5 shrink-0">{toastIcons[toast.type]}</span>
        <span className="whitespace-pre-line leading-snug min-w-0 break-words">{toast.message}</span>
        {toast.action && (
          <button
            type="button"
            className="chatx-toast-action shrink-0"
            onClick={() => {
              toast.action?.onClick();
              toastStore.remove(toast.id);
            }}
          >
            {toast.action.label}
            {/* seconds left to click it — the operator asked to see how long the undo stays */}
            <span className="chatx-toast-action-count" aria-hidden="true">{seconds}</span>
          </button>
        )}
      </div>
      <button
        onClick={() => toastStore.remove(toast.id)}
        className="transition-colors cursor-pointer"
      >
        <X size={16} />
      </button>
      {hasAction && total > 0 && (
        <span className="chatx-toast-countdown" aria-hidden="true">
          <span className="chatx-toast-countdown-bar" style={{ transform: `scaleX(${progress})` }} />
        </span>
      )}
    </motion.div>
  );
};

export const ToastContainer: React.FC = observer(() => {
  return (
    <div className="fixed top-6 right-6 flex flex-col gap-2 items-end" style={{ zIndex: 'var(--z-toast)' }}>
      <AnimatePresence>
        {toastStore.toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} />
        ))}
      </AnimatePresence>
    </div>
  );
});
